import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { navigate } from "../../app/router";
import {
  assessTransferInputs,
  formatBytes,
  preparePackage,
  type TransferInputFile,
  type TransferLimits,
} from "../../packaging";
import {
  buildInvitationUrl,
  createDataSegmentRanges,
  createTransferFrames,
  decodeTransferFrame,
  generateSessionId,
  isValidSessionId,
  shortSessionCode,
  TransferFramePlayback,
  type DataSegmentRange,
  type PlaybackSnapshot,
  type TransferFrame,
  type TransferFrameSet,
} from "../../protocol";
import {
  estimatePreparedTransfer,
  estimateUnpreparedTransfer,
  formatDuration,
} from "./estimates";
import { QrCanvas } from "./QrCanvas";
import { QrSessionStore, type SenderQrSessionPatch } from "./receiverProgressStore";
import {
  DEFAULT_QR_FRAME_DURATION_MS,
  QR_PLAYBACK_SPEED_OPTIONS,
} from "./speedOptions";
import { useTransferLimits } from "./useTransferLimits";

const EMPTY_LIMIT_OVERRIDES: Partial<TransferLimits> = {};

type SenderStatus =
  | "reviewing-files"
  | "preparing"
  | "restoring-session"
  | "invitation-ready"
  | "ready-confirmed"
  | "streaming"
  | "paused"
  | "cancelled"
  | "recoverable-error";

interface PreparedSenderTransfer {
  frameSet: TransferFrameSet;
  invitationUrl: string;
  playback: TransferFramePlayback;
  fileCount: number;
}

export interface SenderTransferProps {
  initialFiles?: TransferInputFile[];
  sessionId?: string;
  resumeSessionId?: string;
  baseUrl?: string;
  limits?: Partial<TransferLimits>;
}

function framesForTarget(
  frameSet: TransferFrameSet,
  segment: DataSegmentRange | undefined,
): readonly TransferFrame[] {
  if (segment === undefined) {
    return frameSet.frames;
  }
  return [
    ...frameSet.metadataFrames,
    ...frameSet.dataFrames.slice(segment.startDataIndex, segment.endDataIndexExclusive),
  ];
}

export function SenderTransfer({
  initialFiles = [],
  sessionId,
  resumeSessionId,
  baseUrl,
  limits: limitOverrides = EMPTY_LIMIT_OVERRIDES,
}: SenderTransferProps) {
  const limits = useTransferLimits(limitOverrides);
  const sessionStore = useMemo(() => new QrSessionStore(), []);
  const closeTimerRef = useRef<number | undefined>(undefined);
  const [files, setFiles] = useState<TransferInputFile[]>(() => [...initialFiles]);
  const [status, setStatus] = useState<SenderStatus>("reviewing-files");
  const [prepared, setPrepared] = useState<PreparedSenderTransfer>();
  const [receiverReady, setReceiverReady] = useState(false);
  const [frameDurationMs, setFrameDurationMs] = useState(DEFAULT_QR_FRAME_DURATION_MS);
  const [playbackSnapshot, setPlaybackSnapshot] = useState<PlaybackSnapshot>();
  const [renderedFrameId, setRenderedFrameId] = useState<string>();
  const [holdRevision, setHoldRevision] = useState(0);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<number>();
  const [error, setError] = useState<string>();
  const [storageWarning, setStorageWarning] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const assessment = useMemo(
    () => assessTransferInputs(files, limits),
    [files, limits],
  );
  const unpreparedEstimate = useMemo(
    () =>
      estimateUnpreparedTransfer(
        assessment.totalBytes,
        assessment.fileCount,
        frameDurationMs,
        limits,
      ),
    [assessment.fileCount, assessment.totalBytes, frameDurationMs, limits],
  );
  const fullPreparedEstimate =
    prepared === undefined
      ? undefined
      : estimatePreparedTransfer(
          prepared.frameSet.frames.length,
          frameDurationMs,
          limits,
        );
  const segments = useMemo(
    () =>
      prepared === undefined
        ? []
        : createDataSegmentRanges(prepared.frameSet.dataFrames.length),
    [prepared],
  );
  const selectedSegment =
    selectedSegmentIndex === undefined ? undefined : segments[selectedSegmentIndex];
  const targetFrameCount = prepared?.playback.frames.length;
  const targetEstimate =
    targetFrameCount === undefined
      ? undefined
      : estimatePreparedTransfer(targetFrameCount, frameDurationMs, limits);
  const exceedsEstimatedChunkLimit =
    unpreparedEstimate.estimatedFrames > limits.maxChunkCount + 1;
  const hasLongEstimate = unpreparedEstimate.state === "long";
  const selectedSpeed = QR_PLAYBACK_SPEED_OPTIONS.find(
    (option) => option.durationMs === frameDurationMs,
  );
  const currentPlaybackFrameId = playbackSnapshot?.currentFrame.id;
  const currentFrameDurationMs = playbackSnapshot?.frameDurationMs;
  const playbackStatus = playbackSnapshot?.status;

  useEffect(() => {
    if (resumeSessionId === undefined) {
      return;
    }

    let cancelled = false;
    void (async () => {
      await Promise.resolve();
      if (cancelled) {
        return;
      }
      if (!isValidSessionId(resumeSessionId)) {
        setError("That paused sender session link is invalid.");
        setStatus("recoverable-error");
        return;
      }
      setStatus("restoring-session");
      setError(undefined);
      setStorageWarning(undefined);
      try {
        const saved = await sessionStore.loadSenderSession(resumeSessionId);
        if (cancelled) {
          return;
        }
        if (saved === undefined) {
          throw new Error(
            "This paused sender session is no longer stored on this device.",
          );
        }

        const decodedFrames = await Promise.all(
          saved.frameTexts.map(async (text) => decodeTransferFrame(text, limits)),
        );
        if (cancelled) {
          return;
        }
        const metadataFrames = decodedFrames
          .filter((frame) => frame.kind === "metadata")
          .sort((left, right) => left.index - right.index);
        const dataFrames = decodedFrames
          .filter((frame) => frame.kind === "data")
          .sort((left, right) => left.index - right.index);
        const firstFrame = decodedFrames[0];
        if (
          firstFrame === undefined ||
          firstFrame.sessionId !== resumeSessionId ||
          metadataFrames.length !== firstFrame.metadataFrameCount ||
          dataFrames.length !== firstFrame.dataFrameCount
        ) {
          throw new Error("The saved sender frame set is incomplete.");
        }

        const frames = [...metadataFrames, ...dataFrames];
        const frameSet: TransferFrameSet = {
          sessionId: resumeSessionId,
          packageSha256: firstFrame.packageSha256,
          packageByteLength: firstFrame.packageByteLength,
          descriptor: {
            version: 1,
            packageByteLength: firstFrame.packageByteLength,
            packageSha256: firstFrame.packageSha256,
            fileCount: saved.record.fileCount,
          },
          metadataFrames,
          dataFrames,
          frames,
        };
        const playback = new TransferFramePlayback(
          frames,
          saved.record.frameDurationMs,
        );
        const invitationUrl =
          baseUrl === undefined
            ? buildInvitationUrl(resumeSessionId)
            : buildInvitationUrl(resumeSessionId, baseUrl);
        setFiles([]);
        setPrepared({
          frameSet,
          invitationUrl,
          playback,
          fileCount: saved.record.fileCount,
        });
        setFrameDurationMs(saved.record.frameDurationMs);
        setSelectedSegmentIndex(undefined);
        setRenderedFrameId(undefined);
        setHoldRevision((revision) => revision + 1);
        setReceiverReady(false);

        if (saved.record.status === "prepared") {
          setPlaybackSnapshot(undefined);
          setStatus("invitation-ready");
        } else {
          playback.start();
          setPlaybackSnapshot(playback.pause());
          setStatus("paused");
          setNotice("Saved sender restored. Recovery starts at Entire transfer.");
          await sessionStore.updateSenderSession(resumeSessionId, {
            status: "paused",
          });
        }
      } catch (caught) {
        if (!cancelled) {
          setPrepared(undefined);
          setPlaybackSnapshot(undefined);
          setError(
            caught instanceof Error
              ? caught.message
              : "The paused sender session could not be restored.",
          );
          setStatus("recoverable-error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [baseUrl, limits, resumeSessionId, sessionStore]);

  useEffect(() => {
    if (closeTimerRef.current !== undefined) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
    return () => {
      closeTimerRef.current = window.setTimeout(() => {
        void sessionStore.close();
      }, 0);
    };
  }, [sessionStore]);

  useEffect(() => {
    if (
      prepared === undefined ||
      playbackStatus !== "streaming" ||
      currentPlaybackFrameId === undefined ||
      currentFrameDurationMs === undefined ||
      renderedFrameId !== currentPlaybackFrameId
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      const advanced = prepared.playback.advance();
      if (advanced.currentFrame.id === currentPlaybackFrameId) {
        setHoldRevision((revision) => revision + 1);
      } else {
        setRenderedFrameId(undefined);
      }
      setPlaybackSnapshot(advanced);
    }, currentFrameDurationMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [
    currentFrameDurationMs,
    currentPlaybackFrameId,
    playbackStatus,
    prepared,
    renderedFrameId,
    holdRevision,
  ]);

  useEffect(() => {
    function handleVisibilityChange(): void {
      if (
        document.visibilityState !== "hidden" ||
        status !== "streaming" ||
        prepared === undefined
      ) {
        return;
      }
      setPlaybackSnapshot(prepared.playback.pause());
      setStatus("paused");
      setNotice(
        "Sending paused while this page was in the background. Reconfirm the receiver before resuming.",
      );
      setReceiverReady(false);
      void sessionStore
        .updateSenderSession(prepared.frameSet.sessionId, { status: "paused" })
        .catch(() => {
          setStorageWarning(
            "The background pause could not be saved. Keep this page open to continue.",
          );
        });
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [prepared, sessionStore, status]);

  function replaceFiles(nextFiles: TransferInputFile[]): void {
    setFiles(nextFiles);
    setPrepared(undefined);
    setPlaybackSnapshot(undefined);
    setRenderedFrameId(undefined);
    setHoldRevision(0);
    setSelectedSegmentIndex(undefined);
    setReceiverReady(false);
    setStatus("reviewing-files");
    setError(undefined);
    setNotice(undefined);
  }

  function handleFileSelection(event: ChangeEvent<HTMLInputElement>): void {
    const selectedFiles = Array.from(event.currentTarget.files ?? []).map(
      (file): TransferInputFile => ({
        name: file.name,
        type: file.type,
        blob: file,
      }),
    );
    if (selectedFiles.length > 0) {
      replaceFiles([...files, ...selectedFiles]);
    }
    event.currentTarget.value = "";
  }

  async function prepare(): Promise<void> {
    if (
      assessment.state === "empty" ||
      assessment.state === "over-cap" ||
      exceedsEstimatedChunkLimit
    ) {
      return;
    }
    setStatus("preparing");
    setError(undefined);
    setNotice(undefined);

    try {
      const nextSessionId =
        sessionId !== undefined && isValidSessionId(sessionId)
          ? sessionId
          : generateSessionId();
      const preparedPackage = await preparePackage(files, limits);
      const frameSet = await createTransferFrames(
        {
          sessionId: nextSessionId,
          packageBytes: preparedPackage.bytes,
          packageSha256: preparedPackage.packageSha256,
          fileCount: preparedPackage.manifest.fileCount,
        },
        { limits },
      );
      const invitationUrl =
        baseUrl === undefined
          ? buildInvitationUrl(nextSessionId)
          : buildInvitationUrl(nextSessionId, baseUrl);
      const playback = new TransferFramePlayback(frameSet.frames, frameDurationMs);
      const fileNames = preparedPackage.manifest.files.map((file) => file.name);
      const displayName =
        fileNames.length === 1
          ? (fileNames[0] ?? "QR transfer")
          : `${fileNames.length.toLocaleString()} files`;
      try {
        await sessionStore.saveSenderSession({
          sessionId: nextSessionId,
          displayName,
          fileCount: preparedPackage.manifest.fileCount,
          preparedBytes: frameSet.packageByteLength,
          frameDurationMs,
          frameTexts: frameSet.frames.map((frame) => frame.text),
          status: "prepared",
        });
        setStorageWarning(undefined);
      } catch {
        setStorageWarning(
          "This transfer is ready, but its sender session could not be saved for navigation or reload recovery. Keep this page open.",
        );
      }
      setPrepared({
        frameSet,
        invitationUrl,
        playback,
        fileCount: preparedPackage.manifest.fileCount,
      });
      setSelectedSegmentIndex(undefined);
      setStatus("invitation-ready");
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The transfer could not be prepared.",
      );
      setStatus("recoverable-error");
    }
  }

  function updateStoredSenderSession(patch: SenderQrSessionPatch): void {
    const preparedSessionId = prepared?.frameSet.sessionId;
    if (preparedSessionId === undefined) {
      return;
    }
    void sessionStore
      .updateSenderSession(preparedSessionId, patch)
      .then((record) => {
        if (record === undefined) {
          setStorageWarning(
            "This sender session is no longer saved. Keep this page open to continue.",
          );
        }
      })
      .catch(() => {
        setStorageWarning(
          "The latest sender state could not be saved. Keep this page open to continue.",
        );
      });
  }

  function changeSpeed(nextDurationMs: number): void {
    setFrameDurationMs(nextDurationMs);
    if (prepared !== undefined) {
      setPlaybackSnapshot(prepared.playback.setFrameDuration(nextDurationMs));
      setHoldRevision((revision) => revision + 1);
      updateStoredSenderSession({ frameDurationMs: nextDurationMs });
    }
  }

  function changeLoopTarget(nextSegmentIndex: number | undefined): void {
    if (prepared === undefined) {
      return;
    }
    const nextSegment =
      nextSegmentIndex === undefined ? undefined : segments[nextSegmentIndex];
    if (nextSegmentIndex !== undefined && nextSegment === undefined) {
      return;
    }

    const nextPlayback = new TransferFramePlayback(
      framesForTarget(prepared.frameSet, nextSegment),
      frameDurationMs,
    );
    let nextSnapshot: PlaybackSnapshot;
    if (status === "streaming") {
      nextSnapshot = nextPlayback.start();
    } else {
      nextPlayback.start();
      nextSnapshot = nextPlayback.pause();
    }

    const currentFrameId = playbackSnapshot?.currentFrame.id;
    const canReuseRenderedFrame =
      currentFrameId !== undefined &&
      renderedFrameId === currentFrameId &&
      nextSnapshot.currentFrame.id === currentFrameId;
    setPrepared({ ...prepared, playback: nextPlayback });
    setSelectedSegmentIndex(nextSegmentIndex);
    setRenderedFrameId(canReuseRenderedFrame ? currentFrameId : undefined);
    setHoldRevision((revision) => revision + 1);
    setPlaybackSnapshot(nextSnapshot);
  }

  function startSending(): void {
    if (prepared === undefined || !receiverReady) {
      return;
    }
    setStatus("streaming");
    setNotice(undefined);
    setRenderedFrameId(undefined);
    setHoldRevision((revision) => revision + 1);
    setPlaybackSnapshot(prepared.playback.start());
    updateStoredSenderSession({ status: "streaming" });
  }

  function pauseSending(): void {
    if (prepared === undefined) {
      return;
    }
    setPlaybackSnapshot(prepared.playback.pause());
    setStatus("paused");
    setNotice("Sending paused. This session can be reopened from Activity.");
    updateStoredSenderSession({ status: "paused" });
  }

  function resumeSending(): void {
    if (prepared === undefined || !receiverReady) {
      return;
    }
    setPlaybackSnapshot(prepared.playback.resume());
    setStatus("streaming");
    setNotice(undefined);
    updateStoredSenderSession({ status: "streaming" });
  }

  async function cancelSession(): Promise<void> {
    const preparedSessionId = prepared?.frameSet.sessionId;
    try {
      if (preparedSessionId !== undefined) {
        await sessionStore.deleteSession("sender", preparedSessionId);
      }
      setPrepared(undefined);
      setPlaybackSnapshot(undefined);
      setRenderedFrameId(undefined);
      setHoldRevision(0);
      setSelectedSegmentIndex(undefined);
      setReceiverReady(false);
      setStorageWarning(undefined);
      setNotice(undefined);
      setStatus("cancelled");
    } catch {
      setStorageWarning(
        "This local sender session could not be deleted. Check browser storage access and try again.",
      );
    }
  }

  const showInvitation =
    prepared !== undefined &&
    (status === "invitation-ready" || status === "ready-confirmed");
  const showStream =
    prepared !== undefined &&
    playbackSnapshot !== undefined &&
    (status === "streaming" || status === "paused");

  return (
    <section
      aria-labelledby="qr-sender-title"
      className="qr-transfer__panel qr-transfer__sender"
    >
      <header className="qr-transfer__header">
        <div>
          <p className="qr-transfer__eyebrow">Local-only QR</p>
          <h2 id="qr-sender-title">Send files by QR</h2>
        </div>
        <span className="qr-transfer__status">
          {showStream
            ? playbackSnapshot.allFramesDisplayed
              ? selectedSegment === undefined
                ? "All frames displayed"
                : `Segment ${String(selectedSegment.index + 1)} looped`
              : status === "paused"
                ? "Paused"
                : "Sending"
            : status === "preparing"
              ? "Preparing locally"
              : status === "restoring-session"
                ? "Restoring session"
                : showInvitation
                  ? "Ready to invite"
                  : "File review"}
        </span>
      </header>

      <p className="qr-transfer__privacy">
        QR mode processes files on these devices and does not upload them. Anyone who
        can clearly record the QR stream may be able to reconstruct an unencrypted
        transfer.
      </p>
      <div className="qr-transfer__recovery-link">
        <span>Prepared and paused QR sessions stay available in Activity.</span>
        <button
          className="qr-transfer__button qr-transfer__button--quiet"
          onClick={() => navigate("/activity")}
          type="button"
        >
          Open saved QR sessions
        </button>
      </div>

      {storageWarning === undefined ? null : (
        <p className="qr-transfer__message qr-transfer__message--warning">
          {storageWarning}
        </p>
      )}
      {notice === undefined ? null : (
        <p aria-live="polite" className="qr-transfer__message">
          {notice}
        </p>
      )}

      {status === "restoring-session" ? (
        <p aria-live="polite" className="qr-transfer__message">
          Restoring and validating the saved sender frames on this device…
        </p>
      ) : null}

      {!showInvitation && !showStream && status !== "restoring-session" ? (
        <>
          <label className="qr-transfer__file-picker">
            <span>Select files</span>
            <input type="file" multiple onChange={handleFileSelection} />
          </label>

          {files.length === 0 ? (
            <p className="qr-transfer__empty">
              Choose a small document, image, or other file to begin.
            </p>
          ) : (
            <ul className="qr-transfer__file-list">
              {files.map((file, index) => (
                <li
                  key={`${file.name}-${file.blob.size.toString()}-${index.toString()}`}
                >
                  <div>
                    <strong>{assessment.sanitizedNames[index] ?? "file"}</strong>
                    <small>
                      {file.type || "Unknown type"} · {formatBytes(file.blob.size)}
                    </small>
                  </div>
                  <button
                    className="qr-transfer__button qr-transfer__button--quiet"
                    onClick={() => {
                      replaceFiles(files.filter((_, fileIndex) => fileIndex !== index));
                    }}
                    type="button"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="qr-transfer__summary">
            <div>
              <span>Selected</span>
              <strong>
                {assessment.fileCount.toString()}{" "}
                {assessment.fileCount === 1 ? "file" : "files"}
              </strong>
            </div>
            <div>
              <span>Original size</span>
              <strong>{formatBytes(assessment.totalBytes)}</strong>
            </div>
            <div>
              <span>Estimated frames</span>
              <strong>{unpreparedEstimate.estimatedFrames.toLocaleString()}</strong>
            </div>
            <div>
              <span>Estimated minimum loop time</span>
              <strong>{formatDuration(unpreparedEstimate.estimatedSeconds)}</strong>
            </div>
          </div>

          <p className="qr-transfer__hint">
            Actual time also includes QR rendering and depends on camera, screen,
            distance, brightness, and selected speed.
          </p>
          {assessment.messages.map((message) => (
            <p
              className={`qr-transfer__message ${
                assessment.state === "over-cap"
                  ? "qr-transfer__message--danger"
                  : "qr-transfer__message--warning"
              }`}
              key={message}
            >
              {message}
            </p>
          ))}
          {exceedsEstimatedChunkLimit ? (
            <p className="qr-transfer__message qr-transfer__message--danger">
              The estimated frame count exceeds the configured{" "}
              {limits.maxChunkCount.toLocaleString()} data-chunk limit. Remove one or
              more files.
            </p>
          ) : hasLongEstimate && assessment.state !== "long" ? (
            <p className="qr-transfer__message qr-transfer__message--warning">
              This transfer may take a long time at the current speed. A cable or local
              wireless method may be more practical.
            </p>
          ) : null}
          {error === undefined ? null : (
            <p className="qr-transfer__message qr-transfer__message--danger">{error}</p>
          )}

          <button
            className="qr-transfer__button qr-transfer__button--primary"
            disabled={
              assessment.state === "empty" ||
              assessment.state === "over-cap" ||
              exceedsEstimatedChunkLimit ||
              status === "preparing"
            }
            onClick={() => {
              void prepare();
            }}
            type="button"
          >
            {status === "preparing" ? "Preparing locally" : "Prepare transfer"}
          </button>
        </>
      ) : null}

      {showInvitation ? (
        <div className="qr-transfer__stage">
          <QrCanvas label="Invitation QR code" value={prepared.invitationUrl} />
          <div className="qr-transfer__stage-copy">
            <h3>Scan invitation</h3>
            <p>
              Ask the receiver to scan this code with the phone camera. Opening the page
              does not notify this device.
            </p>
            <dl className="qr-transfer__details">
              <div>
                <dt>Session</dt>
                <dd>{shortSessionCode(prepared.frameSet.sessionId)}</dd>
              </div>
              <div>
                <dt>Prepared size</dt>
                <dd>{formatBytes(prepared.frameSet.packageByteLength)}</dd>
              </div>
              <div>
                <dt>Files</dt>
                <dd>{prepared.fileCount.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Frames</dt>
                <dd>{prepared.frameSet.frames.length.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Minimum loop time</dt>
                <dd>
                  {formatDuration(
                    fullPreparedEstimate?.estimatedSeconds ??
                      unpreparedEstimate.estimatedSeconds,
                  )}
                </dd>
              </div>
            </dl>
            <label className="qr-transfer__check">
              <input
                checked={receiverReady}
                onChange={(event) => {
                  setReceiverReady(event.currentTarget.checked);
                  setStatus(
                    event.currentTarget.checked
                      ? "ready-confirmed"
                      : "invitation-ready",
                  );
                }}
                type="checkbox"
              />
              <span>The receiver shows Camera ready</span>
            </label>
            <div className="qr-transfer__actions">
              <button
                className="qr-transfer__button qr-transfer__button--primary"
                disabled={!receiverReady}
                onClick={startSending}
                type="button"
              >
                Start sending
              </button>
              <button
                className="qr-transfer__button qr-transfer__button--secondary"
                onClick={() => {
                  void cancelSession();
                }}
                type="button"
              >
                Cancel session
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showStream ? (
        <div className="qr-transfer__stage">
          <QrCanvas
            label={`Target frame ${(playbackSnapshot.currentIndex + 1).toString()} of ${prepared.playback.frames.length.toString()}`}
            onRendered={(renderedValue) => {
              if (renderedValue === playbackSnapshot.currentFrame.text) {
                setRenderedFrameId(playbackSnapshot.currentFrame.id);
              }
            }}
            value={playbackSnapshot.currentFrame.text}
          />
          <div className="qr-transfer__stage-copy">
            <h3>
              {playbackSnapshot.allFramesDisplayed
                ? selectedSegment === undefined
                  ? "All frames displayed"
                  : `Segment ${String(selectedSegment.index + 1)} loop displayed`
                : status === "paused"
                  ? "Sending paused"
                  : "Sending frames"}
            </h3>
            <p>
              {playbackSnapshot.allFramesDisplayed
                ? selectedSegment === undefined
                  ? 'Keep sending until the receiver shows "File verified."'
                  : `Keep this target looping until the receiver marks Segment ${String(selectedSegment.index + 1)} complete.`
                : "Keep the entire code visible and increase screen brightness if scanning is unreliable."}
            </p>
            <dl className="qr-transfer__details">
              <div>
                <dt>Current frame</dt>
                <dd>
                  {(playbackSnapshot.currentIndex + 1).toLocaleString()} /{" "}
                  {prepared.playback.frames.length.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt>Loops displayed</dt>
                <dd>{playbackSnapshot.loopCount.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Session</dt>
                <dd>{shortSessionCode(prepared.frameSet.sessionId)}</dd>
              </div>
              <div>
                <dt>Minimum loop time</dt>
                <dd>
                  {formatDuration(
                    targetEstimate?.estimatedSeconds ??
                      unpreparedEstimate.estimatedSeconds,
                  )}
                </dd>
              </div>
            </dl>
            {segments.length > 1 ? (
              <label className="qr-transfer__field">
                <span>Loop target</span>
                <select
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    changeLoopTarget(value === "all" ? undefined : Number(value));
                  }}
                  value={selectedSegmentIndex ?? "all"}
                >
                  <option value="all">
                    Entire transfer (
                    {prepared.frameSet.dataFrames.length.toLocaleString()} data frames)
                  </option>
                  {segments.map((segment) => (
                    <option key={segment.index} value={segment.index}>
                      Segment {segment.index + 1} · data frames{" "}
                      {segment.startDataIndex + 1}–{segment.endDataIndexExclusive}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {segments.length > 1 ? (
              <p className="qr-transfer__hint">
                Ask the receiver which segment is still missing. Every target repeats
                the transfer metadata, and switching targets keeps every frame ID
                unchanged.
              </p>
            ) : null}
            <label className="qr-transfer__field">
              <span>Playback speed</span>
              <select
                onChange={(event) => {
                  changeSpeed(Number(event.currentTarget.value));
                }}
                value={frameDurationMs}
              >
                {QR_PLAYBACK_SPEED_OPTIONS.map((option) => (
                  <option key={option.durationMs} value={option.durationMs}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {selectedSpeed?.experimental === true ? (
              <p className="qr-transfer__message qr-transfer__message--warning">
                Experimental speeds can be harder for phone cameras to scan. Slow down
                if receiver progress stalls or frames are missed.
              </p>
            ) : null}
            {targetEstimate?.state === "long" ? (
              <p className="qr-transfer__message qr-transfer__message--warning">
                At this speed, frame holds alone take{" "}
                {formatDuration(targetEstimate.estimatedSeconds)}. QR rendering adds
                time. Slowing down can improve scanning but increases transfer time.
              </p>
            ) : null}
            {status === "paused" && !receiverReady ? (
              <label className="qr-transfer__check">
                <input
                  checked={receiverReady}
                  onChange={(event) => {
                    setReceiverReady(event.currentTarget.checked);
                  }}
                  type="checkbox"
                />
                <span>The receiver is ready to continue this saved session</span>
              </label>
            ) : null}
            <div className="qr-transfer__actions">
              {status === "paused" ? (
                <button
                  className="qr-transfer__button qr-transfer__button--primary"
                  disabled={!receiverReady}
                  onClick={resumeSending}
                  type="button"
                >
                  Resume
                </button>
              ) : (
                <button
                  className="qr-transfer__button qr-transfer__button--secondary"
                  onClick={pauseSending}
                  type="button"
                >
                  Pause
                </button>
              )}
              <button
                className="qr-transfer__button qr-transfer__button--secondary"
                onClick={() => {
                  const restarted = prepared.playback.restartLoop();
                  const currentFrameId = playbackSnapshot.currentFrame.id;
                  setRenderedFrameId(
                    renderedFrameId === currentFrameId &&
                      restarted.currentFrame.id === currentFrameId
                      ? currentFrameId
                      : undefined,
                  );
                  setHoldRevision((revision) => revision + 1);
                  setPlaybackSnapshot(restarted);
                }}
                type="button"
              >
                Restart target loop
              </button>
              <button
                className="qr-transfer__button qr-transfer__button--quiet"
                onClick={() => {
                  void cancelSession();
                }}
                type="button"
              >
                Cancel session
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
